import React, { useState, useMemo, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTheme } from "../../../ThemeContext";
import { useFetchProjectEarningsQuery } from "../../../slice/logdetailSlice";
import ContractEarningsView from "./ContractEarningsView";
import FixedCostEarningsView from "./FixedCostEarningsView";

export const getDataGridSx = (isDarkMode) => ({
  border: "none",
  borderTop: isDarkMode ? "1px solid #374151" : "1px solid #e0e0e0",
  "& .MuiDataGrid-main": {
    overflow: "hidden",
  },
  "& .MuiDataGrid-scrollbar, & .MuiDataGrid-scrollbar--vertical, & .MuiDataGrid-scrollbar--horizontal": {
    display: "none !important",
    width: "0 !important",
    height: "0 !important",
    visibility: "hidden !important",
    opacity: 0,
    pointerEvents: "none",
  },
  "& .MuiDataGrid-scrollbarContent": {
    display: "none !important",
  },
  "& .MuiDataGrid-virtualScroller": {
    scrollbarWidth: "thin !important",
    scrollbarColor: "transparent transparent !important",
    transition: "scrollbar-color 0.2s ease-in-out",
  },
  "&:hover .MuiDataGrid-virtualScroller": {
    scrollbarColor: isDarkMode ? "#4b5563 transparent !important" : "#cbd5e1 transparent !important",
  },
  "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
    width: "4px !important",
    height: "4px !important",
  },
  "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": {
    backgroundColor: "transparent !important",
    borderRadius: "4px !important",
    transition: "background-color 0.2s ease-in-out",
  },
  "&:hover .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": {
    backgroundColor: isDarkMode ? "#4b5563 !important" : "#cbd5e1 !important",
  },
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: isDarkMode ? "#262626" : "#f8fafc",
    borderBottom: isDarkMode ? "1px solid #374151" : "1px solid #e0e0e0",
    fontSize: "13px",
    fontWeight: 600,
    color: isDarkMode ? "#9ca3af" : "#6b7280",
  },
  "& .MuiDataGrid-columnHeaderTitle": {
    fontWeight: 600,
  },
  "& .MuiDataGrid-cell": {
    fontSize: "13px",
    borderBottom: isDarkMode ? "1px solid #2d2d2d" : "1px solid #f0f0f0",
    display: "flex",
    alignItems: "center",
    color: isDarkMode ? "#f1f5f9" : "#374151",
  },
  "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
    outline: "none !important",
  },
  "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within": {
    outline: "none !important",
  },
  "& .MuiDataGrid-row:hover": {
    backgroundColor: isDarkMode ? "rgba(255, 255, 255, 0.04)" : "#fafafa",
  },
  "& .active-run-row": {
    backgroundColor: isDarkMode ? "rgba(4, 161, 137, 0.15) !important" : "rgba(4, 161, 137, 0.05) !important",
  },
  "& .active-run-row:hover": {
    backgroundColor: isDarkMode ? "rgba(4, 161, 137, 0.22) !important" : "rgba(4, 161, 137, 0.09) !important",
  },
  "& .MuiDataGrid-footerContainer": {
    borderTop: isDarkMode ? "1px solid #374151" : "1px solid #e0e0e0",
    minHeight: "48px",
  },
});

const ProjectEarningsTab = ({ projectDetail, projectId: propProjectId }) => {
  const { isDarkMode } = useTheme();
  const dataGridSx = useMemo(() => getDataGridSx(isDarkMode), [isDarkMode]);

  const { id } = useParams();
  const projectId =
    propProjectId ||
    projectDetail?.project_id ||
    id ||
    localStorage.getItem("project_id");

  const {
    data: earningsApiResponse,
    isLoading: isEarningsLoading,
    isFetching: isEarningsFetching,
    refetch: refetchEarnings,
  } = useFetchProjectEarningsQuery(
    { project_id: projectId },
    {
      skip: !projectId,
      refetchOnMountOrArgChange: true,
    }
  );

  const [showLoader, setShowLoader] = useState(true);

  useEffect(() => {
    setShowLoader(true);
    if (projectId && typeof refetchEarnings === "function") {
      refetchEarnings();
    }
    const timer = setTimeout(() => {
      setShowLoader(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, [projectId, refetchEarnings]);

  const earningsData = earningsApiResponse?.data || earningsApiResponse;
  const apiResources = earningsData?.resources;
  const apiSummary = earningsData?.summary;
  const isAllocated = Boolean(earningsData?.is_allocated);
  const apiProject = earningsData?.project;
  const apiInvoices = useMemo(
    () => (Array.isArray(earningsData?.invoices) ? earningsData.invoices : []),
    [earningsData]
  );

  const isFixedCost = useMemo(() => {
    const pType = (
      apiProject?.budget_type ||
      projectDetail?.budget_type ||
      projectDetail?.billing_type ||
      ""
    ).toUpperCase();
    return pType.includes("FIXED") || pType === "FIXED_COST";
  }, [apiProject, projectDetail]);

  const isContract = useMemo(() => {
    const pType = (
      apiProject?.budget_type ||
      projectDetail?.budget_type ||
      projectDetail?.billing_type ||
      ""
    ).toUpperCase();
    return (
      pType === "CONTRACT" ||
      pType.includes("CONTRACT") ||
      pType.includes("TIME_AND_MATERIAL") ||
      pType === "T&M" ||
      (!isFixedCost && apiInvoices.length > 0)
    );
  }, [apiProject, projectDetail, isFixedCost, apiInvoices]);

  const kpiData = useMemo(() => {
    let contractVal = "Not Set";
    const budgetValue =
      apiSummary?.total_budget ??
      apiProject?.budget ??
      apiProject?.total_budget ??
      projectDetail?.budget ??
      projectDetail?.total_budget;

    const hasBudget = budgetValue != null && Number(budgetValue) > 0;

    if (hasBudget) {
      contractVal = `₹${Number(budgetValue).toLocaleString("en-IN")}`;
    } else if (apiSummary?.total_received != null && Number(apiSummary.total_received) > 0) {
      contractVal = `₹${Number(apiSummary.total_received).toLocaleString("en-IN")}`;
    } else if (apiSummary?.total_earning != null && Number(apiSummary.total_earning) > 0) {
      contractVal = `₹${Number(apiSummary.total_earning).toLocaleString("en-IN")}`;
    } else {
      contractVal = isContract ? "Not Set" : "₹0";
    }

    const contractSub = isContract
      ? hasBudget
        ? "Agreed budget"
        : Number(apiSummary?.total_received || 0) > 0
        ? "Total billed to date"
        : "Budget not set"
      : "Base scope budget";

    const receivedVal =
      apiSummary?.total_received != null
        ? `₹${Number(apiSummary.total_received).toLocaleString("en-IN")}`
        : "₹0";

    const costVal =
      apiSummary?.total_cost != null
        ? `₹${Number(apiSummary.total_cost).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        : "₹0.00";

    let marginVal = "—";
    if (apiSummary) {
      const rec = Number(apiSummary.total_received || apiSummary.total_earning || 0);
      const c = Number(apiSummary.total_cost || 0);
      if (c > 0) {
        marginVal = `${(rec / c).toFixed(2)}x`;
      } else {
        marginVal = "—";
      }
    }

    const hasAllocatedInvoices = isContract && apiInvoices.length > 0;
    const earningsStatusText = isContract
      ? hasAllocatedInvoices
        ? "Allocated"
        : "Not Allocated"
      : isAllocated
      ? "Allocated"
      : "Pending completion";

    const earningsStatusSub = isContract
      ? hasAllocatedInvoices
        ? `${apiInvoices.length} ${apiInvoices.length === 1 ? "invoice" : "invoices"} allocated`
        : "0 invoices allocated"
      : isAllocated
      ? "Final allocation complete"
      : "Final run at project close";

    const earningsStatusColor = isContract
      ? hasAllocatedInvoices
        ? "text-success"
        : isDarkMode ? "text-light text-opacity-50" : "text-muted"
      : isAllocated
      ? "text-success"
      : "text-warning";

    return {
      contractVal,
      contractSub,
      receivedVal,
      costVal,
      marginVal,
      earningsStatusText,
      earningsStatusSub,
      earningsStatusColor,
    };
  }, [apiSummary, apiProject, projectDetail, isAllocated, isContract, apiInvoices.length, isDarkMode]);

  const isGridLoading = showLoader || isEarningsLoading || isEarningsFetching;

  return (
    <div className="container-fluid p-0 mt-3 project-earnings-container">
      {/* 1. Top KPI Summary Cards */}
      <div className="row g-3 mb-4">
        {/* Contract Value */}
        <div className="col-12 col-sm-6 col-lg">
          <div className={`card h-100 border rounded shadow-sm p-3 ${isDarkMode ? "bg-dark border-secondary" : ""}`}>
            <div
              className={`small fw-semibold text-uppercase ${isDarkMode ? "text-secondary" : "text-muted"}`}
              style={{ fontSize: "11px", letterSpacing: "0.5px" }}
            >
              Contract Value
            </div>
            <div
              className={`fs-4 fw-bold mt-1 ${isDarkMode ? "text-light" : "text-dark"}`}
              style={{ fontSize: "21px" }}
            >
              {kpiData.contractVal}
            </div>
            <div className={`small mt-1 ${isDarkMode ? "text-secondary" : "text-muted"}`}>
              {kpiData.contractSub}
            </div>
          </div>
        </div>

        {/* Received (Mapped) */}
        <div className="col-12 col-sm-6 col-lg">
          <div className={`card h-100 border rounded shadow-sm p-3 ${isDarkMode ? "bg-dark border-secondary" : ""}`}>
            <div
              className={`small fw-semibold text-uppercase ${isDarkMode ? "text-secondary" : "text-muted"}`}
              style={{ fontSize: "11px", letterSpacing: "0.5px" }}
            >
              {isContract ? "Received (Allocated)" : "Received (Mapped)"}
            </div>
            <div className="fs-4 fw-bold text-success mt-1">{kpiData.receivedVal}</div>
            <div className={`small mt-1 ${isDarkMode ? "text-secondary" : "text-muted"}`}>
              {isContract
                ? "From allocated contract invoices"
                : isAllocated
                ? "From allocated receipts"
                : "From mapped receipts"}
            </div>
          </div>
        </div>

        {/* Cost to Date */}
        <div className="col-12 col-sm-6 col-lg">
          <div className={`card h-100 border rounded shadow-sm p-3 ${isDarkMode ? "bg-dark border-secondary" : ""}`}>
            <div
              className={`small fw-semibold text-uppercase ${isDarkMode ? "text-secondary" : "text-muted"}`}
              style={{ fontSize: "11px", letterSpacing: "0.5px" }}
            >
              Cost to Date
            </div>
            <div className={`fs-4 fw-bold mt-1 ${isDarkMode ? "text-light" : "text-dark"}`}>{kpiData.costVal}</div>
            <div className={`small mt-1 ${isDarkMode ? "text-secondary" : "text-muted"}`}>
              {isContract ? "Loaded resource cost" : "Total logged cost"}
            </div>
          </div>
        </div>

        {/* Indicative Margin */}
        <div className="col-12 col-sm-6 col-lg">
          <div className={`card h-100 border rounded shadow-sm p-3 ${isDarkMode ? "bg-dark border-secondary" : ""}`}>
            <div
              className={`small fw-semibold text-uppercase ${isDarkMode ? "text-secondary" : "text-muted"}`}
              style={{ fontSize: "11px", letterSpacing: "0.5px" }}
            >
              {isContract ? "Realised Margin" : "Indicative Margin"}
            </div>
            <div className="fs-4 fw-bold text-success mt-1">{kpiData.marginVal}</div>
            <div className={`small mt-1 ${isDarkMode ? "text-secondary" : "text-muted"}`}>
              {isContract && apiSummary?.total_profit != null
                ? `Profit: ₹${Number(apiSummary.total_profit).toLocaleString("en-IN")}`
                : "Received ÷ Cost to date"}
            </div>
          </div>
        </div>

        {/* Earnings Status */}
        <div className="col-12 col-sm-6 col-lg">
          <div className={`card h-100 border rounded shadow-sm p-3 ${isDarkMode ? "bg-dark border-secondary" : ""}`}>
            <div
              className={`small fw-semibold text-uppercase ${isDarkMode ? "text-secondary" : "text-muted"}`}
              style={{ fontSize: "11px", letterSpacing: "0.5px" }}
            >
              Earnings Status
            </div>
            <div
              className={`fs-4 fw-bold mt-1 text-truncate ${kpiData.earningsStatusColor}`}
              title={kpiData.earningsStatusText}
              style={{ fontSize: "16px" }}
            >
              {kpiData.earningsStatusText}
            </div>
            <div className={`small mt-1 text-truncate ${isDarkMode ? "text-secondary" : "text-muted"}`}>
              {kpiData.earningsStatusSub}
            </div>
          </div>
        </div>
      </div>

      {/* 2. Main Body: Contract View vs Fixed Cost View */}
      {isContract ? (
        <ContractEarningsView
          apiInvoices={apiInvoices}
          isGridLoading={isGridLoading}
          dataGridSx={dataGridSx}
        />
      ) : (
        <FixedCostEarningsView
          apiResources={apiResources}
          apiSummary={apiSummary}
          apiInvoices={apiInvoices}
          isAllocated={isAllocated}
          isGridLoading={isGridLoading}
          dataGridSx={dataGridSx}
        />
      )}
    </div>
  );
};

export default ProjectEarningsTab;
